/**
 * AutomationCard — single card representing one automation.
 *
 * Click the card body opens the detail modal (via `onOpen`).
 * Toggle + inline delete buttons remain functional (use stopPropagation).
 */
import { useState } from "react";
import {
  EnvelopeSimple, FolderSimple, Envelope, Trash, ArrowsClockwise,
  ToggleLeft, ToggleRight, Lightning,
} from "@phosphor-icons/react";

const TEMPLATE_META = {
  scheduled_email:       { label: "Scheduled Email",        icon: EnvelopeSimple, color: "blue" },
  file_upload_alert:     { label: "New File Upload Alert",  icon: FolderSimple,   color: "emerald" },
  email_received_alert:  { label: "New Email Received",     icon: Envelope,       color: "violet" },
  email_project_router:  { label: "Email Project Router",   icon: Lightning,      color: "amber" },
};

const COLOR = {
  blue:    { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-600"    },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-600"  },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-600"   },
};

const FREQUENCY_LABELS = { Day: "Daily", Week: "Weekly", Month: "Monthly" };

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function RunStatus({ run }) {
  if (!run) return <span className="text-[10px] text-slate-400">No runs yet</span>;
  const map = { Succeeded: "text-emerald-600", Failed: "text-red-500", Running: "text-blue-500" };
  return (
    <span className={`text-[10px] font-medium ${map[run.status] || "text-slate-400"}`}>
      {run.status} · {fmtDate(run.start_time)}
    </span>
  );
}

export default function AutomationCard({
  automation,
  onToggle,
  onDelete,
  onOpen,
  toggling,
  deleting,
}) {
  const tmpl = TEMPLATE_META[automation.template_type] || TEMPLATE_META.scheduled_email;
  const Icon = tmpl.icon;
  const c    = COLOR[tmpl.color] || COLOR.blue;
  const cfg  = automation.config || {};
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleCardClick = (e) => {
    if (confirmDelete) return;
    if (e.target.closest("[data-no-card-click]")) return;
    onOpen(automation);
  };

  return (
    <div
      className="bg-white border border-slate-200 p-4 flex flex-col gap-3 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
      data-testid={`automation-card-${automation.id}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !confirmDelete) {
          e.preventDefault();
          onOpen(automation);
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 flex-shrink-0 rounded flex items-center justify-center ${c.bg} ${c.border} border`}>
            <Icon size={15} className={c.text} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{automation.name}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{tmpl.label}</p>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(automation.id); }}
          disabled={!!toggling}
          data-no-card-click
          className="flex-shrink-0 text-slate-400 hover:text-primary transition-colors"
          title={automation.enabled ? "Disable" : "Enable"}
          data-testid={`toggle-${automation.id}`}
        >
          {toggling === automation.id
            ? <ArrowsClockwise size={18} className="animate-spin" />
            : automation.enabled
              ? <ToggleRight size={22} className="text-emerald-500" weight="fill" />
              : <ToggleLeft size={22} className="text-slate-300" weight="fill" />}
        </button>
      </div>

      {/* Description */}
      {automation.description && (
        <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-3" title={automation.description}>
          {automation.description}
        </p>
      )}

      {/* Config summary */}
      <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-50 pt-2 space-y-0.5">
        {cfg.recipient_email && (
          <p><span className="font-medium text-slate-600">To:</span> {cfg.recipient_email}</p>
        )}
        {cfg.subject && (
          <p className="truncate"><span className="font-medium text-slate-600">Subject:</span> {cfg.subject}</p>
        )}
        {cfg.frequency && (
          <p>
            <span className="font-medium text-slate-600">Schedule:</span>{" "}
            {FREQUENCY_LABELS[cfg.frequency] || cfg.frequency}
            {cfg.week_days?.length ? ` · ${cfg.week_days.join(", ")}` : ""}
            {cfg.hour !== undefined ? ` at ${String(cfg.hour).padStart(2, "0")}:00 AEST` : ""}
          </p>
        )}
        {cfg.project_name && (
          <p><span className="font-medium text-slate-600">Folder:</span> {cfg.project_name}</p>
        )}
        {automation.template_type === "email_received_alert" && (
          <>
            <p>
              <span className="font-medium text-slate-600">Trigger:</span> New email in Inbox
              {cfg.only_with_attachments === false ? "" : " (with attachment)"}
            </p>
            {cfg.destination_folder_name && (
              <p><span className="font-medium text-slate-600">Saves to:</span> {cfg.destination_folder_name}</p>
            )}
          </>
        )}
        {automation.template_type !== "email_received_alert" && (
          <p>
            <span className="font-medium text-slate-600">Scope:</span>{" "}
            {automation.project_scope === "all" ? "All projects" : `${automation.project_ids?.length || 0} project(s)`}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-50 pt-2" data-no-card-click>
        <RunStatus run={automation.last_run} />
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(automation.id); }}
              className="text-[11px] font-semibold text-red-600 hover:underline"
              data-testid={`confirm-delete-${automation.id}`}
            >
              {deleting === automation.id ? "Deleting…" : "Confirm"}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
              className="text-[11px] text-slate-400 hover:underline"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
            className="p-1 text-slate-300 hover:text-red-400 transition-colors"
            data-testid={`delete-btn-${automation.id}`}
          >
            <Trash size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
